import { Fragment, useState } from 'react';
import { Chip, TableCell, TableRow } from '@mui/material';

import Button from '@/components/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';
import {
  experimentsAPI,
  extractExperimentsApiError,
  ExperimentDetail,
  ExperimentStatus,
  ExperimentSummary,
} from '@/services/experimentsAPI';

export type Props = {
  flagKey: string;
  experiment: ExperimentSummary;
  onEdit: () => void;
  onChanged: (updated: ExperimentDetail) => void;
};

const STATUS_COLORS: Record<
  ExperimentStatus,
  'default' | 'success' | 'warning' | 'info'
> = {
  draft: 'default',
  active: 'success',
  paused: 'warning',
  completed: 'info',
  archived: 'default',
};

// Mirrors the lifecycle transitions guarded server-side in
// api/admin/experiments_controller.rb (start/pause/complete), each a
// dedicated POST — status is never set via the edit form.
const ExperimentRow = ({ flagKey, experiment, onEdit, onChanged }: Props) => {
  const [busy, setBusy] = useState<'start' | 'pause' | 'complete' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Starting this experiment completes the flag's other active or paused
  // experiment server-side (experiments_controller.rb#start). A completed run
  // keeps its results and read-out, but it can never be started again. Gate
  // Start behind a confirmation that says so, rather than firing on a single
  // click.
  const [confirmingStart, setConfirmingStart] = useState(false);

  const runTransition = async (
    action: 'start' | 'pause' | 'complete',
    call: () => Promise<ExperimentDetail>,
  ) => {
    setBusy(action);
    setError(null);
    try {
      const updated = await call();
      onChanged(updated);
    } catch (err: any) {
      setError(
        extractExperimentsApiError(err, `Failed to ${action} experiment`),
      );
    } finally {
      setBusy(null);
    }
  };

  const handleStart = () =>
    runTransition('start', () =>
      experimentsAPI.startExperiment(flagKey, experiment.id),
    );
  const handlePause = () =>
    runTransition('pause', () =>
      experimentsAPI.pauseExperiment(flagKey, experiment.id),
    );
  const handleComplete = () =>
    runTransition('complete', () =>
      experimentsAPI.completeExperiment(flagKey, experiment.id),
    );

  const canStart =
    experiment.status === 'draft' || experiment.status === 'paused';
  const canPause = experiment.status === 'active';
  const canComplete =
    experiment.status === 'active' || experiment.status === 'paused';

  return (
    // Fragment, not just <TableRow>: ConfirmationModal isn't a portal (see
    // Modal.tsx), so mounting it as a TableRow child would nest a <div>
    // directly under a <tr> — invalid table markup. Rendered as a sibling
    // instead.
    <Fragment>
      <TableRow>
        <TableCell>{experiment.name}</TableCell>
        <TableCell>
          <Small color="lighter">{experiment.key}</Small>
        </TableCell>
        <TableCell>
          <Chip
            label={experiment.status}
            size="small"
            color={STATUS_COLORS[experiment.status]}
          />
        </TableCell>
        <TableCell align="right">{experiment.rollout_percentage}%</TableCell>
        <TableCell align="right">
          <Stack gap={8} justify="end">
            {error && <Small color="danger">{error}</Small>}
            {/* All actions lock while ANY transition is in flight — a paused
                row shows both Start and Complete, and with only the busy
                button disabled a second click could land after the first
                transition resolves (e.g. Complete succeeding on the
                just-started experiment, which is terminal). */}
            <Button
              a11yLabel={`Edit experiment ${experiment.name}`}
              label="Edit"
              appearance="plain"
              disabled={busy !== null}
              onClick={onEdit}
            />
            {canStart && (
              <Button
                a11yLabel={`Start experiment ${experiment.name}`}
                label="Start"
                appearance="plain"
                isLoading={busy === 'start'}
                disabled={busy !== null}
                onClick={() => setConfirmingStart(true)}
              />
            )}
            {canPause && (
              <Button
                a11yLabel={`Pause experiment ${experiment.name}`}
                label="Pause"
                appearance="plain"
                isLoading={busy === 'pause'}
                disabled={busy !== null}
                onClick={handlePause}
              />
            )}
            {canComplete && (
              <Button
                a11yLabel={`Complete experiment ${experiment.name}`}
                label="Complete"
                appearance="plain"
                isLoading={busy === 'complete'}
                disabled={busy !== null}
                onClick={handleComplete}
              />
            )}
          </Stack>
        </TableCell>
      </TableRow>
      {confirmingStart && (
        <ConfirmationModal
          acceptAction={() => {
            setConfirmingStart(false);
            handleStart();
          }}
          acceptButtonLabel="Start experiment"
          bodyContentText={`Starting "${experiment.name}" will complete this flag's other running or paused experiment, if there is one. The completed experiment keeps its results, but it cannot be started again. Continue?`}
          denyAction={() => {}}
          denyButtonLabel="Cancel"
          hideModal={() => setConfirmingStart(false)}
          title="Start experiment?"
        />
      )}
    </Fragment>
  );
};

export default ExperimentRow;
